// Centralized API client with Firebase auth header support
import { auth } from '../firebaseConfig';

const BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
const WITH_CREDENTIALS = String(process.env.REACT_APP_WITH_CREDENTIALS || 'false') === 'true';

async function buildHeaders(extraHeaders = {}) {
  const headers = {
    'Content-Type': 'application/json',
    ...extraHeaders,
  };
  try {
    const currentUser = auth.currentUser;
    if (currentUser) {
      const token = await currentUser.getIdToken();
      headers.Authorization = `Bearer ${token}`;
      // Add a secondary header some backends accept
      headers['X-Auth-Token'] = token;
    }
  } catch (_) {
    // no-op: unauthenticated or token retrieval failed
  }
  return headers;
}

async function request(path, { method = 'GET', body, headers: extraHeaders, signal, _retry } = {}) {
  const headers = await buildHeaders(extraHeaders);
  const url = `${BASE_URL}${path}`;
  let response = await fetch(url, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
    signal,
    ...(WITH_CREDENTIALS ? { credentials: 'include' } : {}),
  });

  // If unauthorized, try one forced token refresh and retry once
  if (response.status === 401 && !_retry) {
    try {
      const currentUser = auth.currentUser;
      if (currentUser) {
        await currentUser.getIdToken(true); // force refresh
        const refreshedHeaders = await buildHeaders(extraHeaders);
        response = await fetch(url, {
          method,
          headers: refreshedHeaders,
          body: body ? JSON.stringify(body) : undefined,
          signal,
          ...(WITH_CREDENTIALS ? { credentials: 'include' } : {}),
        });
      }
    } catch (_) {
      // fall through to error handling below
    }
  }

  if (!response.ok) {
    const contentType = response.headers.get('content-type') || '';
    const errorPayload = contentType.includes('application/json') ? await response.json() : { error: await response.text() };
    const error = new Error(errorPayload?.error || response.statusText || 'Request failed');
    error.status = response.status;
    error.payload = errorPayload;
    throw error;
  }
  const contentType = response.headers.get('content-type') || '';
  if (contentType.includes('application/json')) {
    return response.json();
  }
  return response.text();
}

export const api = {
  get: (path, options) => request(path, { ...options, method: 'GET' }),
  post: (path, body, options) => request(path, { ...options, method: 'POST', body }),
  put: (path, body, options) => request(path, { ...options, method: 'PUT', body }),
  del: (path, body, options) => request(path, { ...options, method: 'DELETE', body }),
};

export default api;


